import React from 'react';
import { getGamesByRegion } from '@/data/tournament';
import { useBracketStore } from '@/stores/bracketStore';
import { projectGame } from '@/hooks/useProjectedGame';
import Matchup from './Matchup';
import ProgressRing from '@/components/gaming/ProgressRing';
import type { BracketRegion } from '@bracketblitz/shared-types';

interface BracketRegionProps {
  region: BracketRegion;
  writeEnabled?: boolean;
  className?: string;
}

const BracketRegionComponent: React.FC<BracketRegionProps> = ({ region, writeEnabled = true, className = '' }) => {
  const regionGames = getGamesByRegion(region);
  
  const playInGames = regionGames.filter(game => game.round === 'first_four');
  const firstRoundGames = regionGames.filter(game => game.round === 'first_round');
  const secondRoundGames = regionGames.filter(game => game.round === 'second_round');
  const sweetSixteenGames = regionGames.filter(game => game.round === 'sweet_sixteen');
  const eliteEightGame = regionGames.find(game => game.round === 'elite_eight');

  const [isMobile, setIsMobile] = React.useState(false);

  const regionInfo = {
    east: {
      name: 'East',
      color: 'from-blue-500 to-blue-700',
      borderColor: 'border-blue-500/20',
      accentColor: 'text-blue-400',
      headerBorder: 'border-t-blue-500',
      headerBg: 'bg-blue-500/5',
      connectorColor: 'bg-blue-500/25',
    },
    west: {
      name: 'West',
      color: 'from-green-500 to-green-700',
      borderColor: 'border-green-500/20',
      accentColor: 'text-green-400',
      headerBorder: 'border-t-green-500',
      headerBg: 'bg-green-500/5',
      connectorColor: 'bg-green-500/25',
    },
    south: {
      name: 'South',
      color: 'from-red-500 to-red-700',
      borderColor: 'border-red-500/20',
      accentColor: 'text-red-400',
      headerBorder: 'border-t-red-500',
      headerBg: 'bg-red-500/5',
      connectorColor: 'bg-red-500/25',
    },
    midwest: {
      name: 'Midwest',
      color: 'from-yellow-500 to-yellow-700',
      borderColor: 'border-yellow-500/20',
      accentColor: 'text-yellow-400',
      headerBorder: 'border-t-yellow-500',
      headerBg: 'bg-yellow-500/5',
      connectorColor: 'bg-yellow-500/25',
    },
  };

  const info = regionInfo[region];

  const getRegionProgress = useBracketStore(s => s.getRegionProgress);
  const picks = useBracketStore(s => s.picks);
  const getPickForGame = useBracketStore(s => s.getPickForGame);
  const regionProgress = getRegionProgress(region).percentage;

  const orderedGamesForConnection = [
    ...playInGames,
    ...firstRoundGames,
    ...secondRoundGames,
    ...sweetSixteenGames,
    ...(eliteEightGame ? [eliteEightGame] : []),
  ].filter(Boolean);

  const getGamesByIdForRegion = (gameId: string) => orderedGamesForConnection.find((g) => g.id === gameId);

  const nextCandidate = orderedGamesForConnection.find((rawGame) => {
    const game = projectGame(rawGame, picks);
    const pick = getPickForGame(game.id);
    const bothTeamsAvailable = !!(game.team1Id && game.team1Id !== 'tbd' && game.team2Id && game.team2Id !== 'tbd');
    return !pick && bothTeamsAvailable;
  });

  const nextGameHintId = nextCandidate?.id || null;

  const isPathSourceForHint = (gameId: string | null | undefined) => {
    if (!gameId || !nextGameHintId) return false;
    const game = getGamesByIdForRegion(gameId);
    return Boolean(game && game.nextGameId === nextGameHintId);
  };

  const scrollToGame = (gameId?: string | null) => {
    if (!gameId || typeof window === 'undefined') return;
    const node = document.getElementById(`matchup-${gameId}`);
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handlePickComplete = (gameId: string) => {
    requestAnimationFrame(() => {
      const state = useBracketStore.getState();
      const latestPicks = state.picks;
      const latestGetPick = state.getPickForGame;

      const all = [
        ...playInGames,
        ...firstRoundGames,
        ...secondRoundGames,
        ...sweetSixteenGames,
        ...(eliteEightGame ? [eliteEightGame] : []),
      ].filter(Boolean);

      const pickedIndex = all.findIndex((g) => g.id === gameId);
      const next = all.slice(pickedIndex + 1).find((rawGame) => {
        const projected = projectGame(rawGame, latestPicks);
        const pick = latestGetPick(rawGame.id);
        const bothTeamsAvailable = !!(projected.team1Id && projected.team1Id !== 'tbd' && projected.team2Id && projected.team2Id !== 'tbd');
        return !pick && bothTeamsAvailable;
      });

      if (next) {
        setTimeout(() => scrollToGame(next.id), 220);
      }
    });
  };

  React.useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const onChange = () => setIsMobile(media.matches);
    onChange();
    media.addEventListener('change', onChange);

    return () => media.removeEventListener('change', onChange);
  }, []);

  return (
    <div
      className={`bg-dark-700/50 ${info.borderColor} border rounded-xl p-3 sm:p-4 md:p-6 ${className}`}
    >
      {/* Region header with progress */}
      <div className="mb-4 sm:mb-6 md:mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2
            className={`text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r ${info.color} bg-clip-text text-transparent`}
          >
            {info.name} Region
          </h2>
          
          <ProgressRing 
            progress={regionProgress}
            size={isMobile ? 'sm' : 'md'}
            color="orange"
            animated
            showPercentage
          />
        </div>
        
        <div className="flex items-center justify-between text-[11px] sm:text-sm">
          <span className="text-text-muted">
            {regionProgress}% Complete • {regionGames.length} Games
          </span>
          <span className={`${info.accentColor} font-medium text-[10px] sm:text-sm`}>
            Road to Final Four
          </span>
        </div>
      </div>

      {/* First Four play-in games for this region */}
      {playInGames.length > 0 && (
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl border border-amber-500/20 bg-amber-500/[0.03]">
          <h3 className="text-[11px] sm:text-sm font-semibold text-amber-400 mb-2 sm:mb-3 flex items-center gap-2">
            🏀 First Four
            <span className="text-[10px] sm:text-[11px] text-amber-400/60 font-normal">Pick the play-in winner to unlock the R1 matchup</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            {playInGames.map(game => (
              <Matchup key={game.id} game={game} size={isMobile ? 'sm' : 'md'} compact={isMobile} showConfidence={false} writeEnabled={Boolean(writeEnabled)} isNextGameHint={nextGameHintId === game.id} isPathSource={isPathSourceForHint(game.id)} onNextMatchup={() => scrollToGame(nextGameHintId)} onPickComplete={handlePickComplete} />
            ))}
          </div>
        </div>
      )}

      {/* Bracket structure — 4-column grid with connector gutters */}
      <div className="grid grid-cols-[1fr_12px_1fr_12px_1fr_12px_1fr] sm:grid-cols-[1fr_16px_1fr_16px_1fr_16px_1fr] gap-y-0 items-stretch">
        {/* ── Round headers ── */}
        <div className="flex items-center justify-center mb-4">
          <div className={`px-4 py-2 rounded-lg border border-dark-400 ${info.headerBg} border-t-2 ${info.headerBorder}`}>
            <h3 className="text-[11px] sm:text-sm font-bold text-white tracking-wide">Round 1</h3>
            <p className="text-[10px] sm:text-[11px] text-text-muted">First Round</p>
          </div>
        </div>
        <div />{/* gutter */}
        <div className="flex items-center justify-center mb-4">
          <div className={`px-4 py-2 rounded-lg border border-dark-400 ${info.headerBg} border-t-2 ${info.headerBorder}`}>
            <h3 className="text-[11px] sm:text-sm font-bold text-white tracking-wide">Round 2</h3>
            <p className="text-[10px] sm:text-[11px] text-text-muted">Second Round</p>
          </div>
        </div>
        <div />{/* gutter */}
        <div className="flex items-center justify-center mb-4">
          <div className={`px-4 py-2 rounded-lg border border-dark-400 ${info.headerBg} border-t-2 ${info.headerBorder}`}>
            <h3 className="text-[11px] sm:text-sm font-bold text-white tracking-wide">Round 3</h3>
            <p className="text-[10px] sm:text-[11px] text-text-muted">Sweet 16</p>
          </div>
        </div>
        <div />{/* gutter */}
        <div className="flex items-center justify-center mb-4">
          <div className="px-4 py-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] border-t-2 border-t-amber-400">
            <h3 className="text-[11px] sm:text-sm font-bold text-amber-400 tracking-wide">Elite 8</h3>
            <p className="text-[10px] sm:text-[11px] text-amber-400/60">Regional Final</p>
          </div>
        </div>

        {/* ── Bracket rows: 8 matchup-pairs ── */}
        {/* Each "pair" = 2 R1 matchups → 1 R2 → feeds into S16 / E8 */}
        {Array.from({ length: 4 }, (_, pairIdx) => {
          const r1a = firstRoundGames[pairIdx * 2];
          const r1b = firstRoundGames[pairIdx * 2 + 1];
          const r2 = secondRoundGames[pairIdx];
          const s16 = sweetSixteenGames[Math.floor(pairIdx / 2)];
          const showS16 = pairIdx % 2 === 0; // only render S16 on even pairs
          const showE8 = pairIdx === 1; // only render E8 once, spanning pairs 0–3

          return (
            <React.Fragment key={pairIdx}>
              {/* ── R1 column: 2 matchups grouped in a bracket pod ── */}
              <div className={`flex flex-col justify-center gap-0 ${pairIdx > 0 ? 'mt-3' : ''}`}>
                <div className={`rounded-xl border ${info.borderColor} bg-dark-600/30 ${isMobile ? 'p-1.5' : 'p-2 sm:p-3'} flex flex-col gap-0`}>
                  {r1a && <Matchup game={r1a} size={isMobile ? 'sm' : 'md'} compact={isMobile} showConfidence writeEnabled={Boolean(writeEnabled)} isNextGameHint={nextGameHintId === r1a.id} isPathSource={isPathSourceForHint(r1a.id)} onNextMatchup={() => scrollToGame(nextGameHintId)} onPickComplete={handlePickComplete} />}
                  {r1a && r1b && (
                    <div className="flex items-center gap-2 py-1.5 px-1 my-1">
                      <div className={`flex-1 h-px ${info.connectorColor}`} />
                      <span className="text-[9px] font-semibold tracking-widest text-white/30 uppercase">vs winner</span>
                      <div className={`flex-1 h-px ${info.connectorColor}`} />
                    </div>
                  )}
                  {r1b && <Matchup game={r1b} size={isMobile ? 'sm' : 'md'} compact={isMobile} showConfidence writeEnabled={Boolean(writeEnabled)} isNextGameHint={nextGameHintId === r1b.id} isPathSource={isPathSourceForHint(r1b.id)} onNextMatchup={() => scrollToGame(nextGameHintId)} onPickComplete={handlePickComplete} />}
                </div>
              </div>

              {/* ── Connector: R1 → R2 ── */}
              <div className="flex items-center justify-center">
                <div className={`w-full h-[2px] ${info.connectorColor} rounded-full`} />
              </div>

              {/* ── R2 column: 1 matchup ── */}
              <div className={`flex flex-col justify-center ${pairIdx > 0 ? (isMobile ? 'mt-2' : 'mt-3') : ''}`}>
                {r2 && <Matchup game={r2} size={isMobile ? 'sm' : 'md'} compact={isMobile} showConfidence writeEnabled={Boolean(writeEnabled)} isNextGameHint={nextGameHintId === r2.id} isPathSource={isPathSourceForHint(r2.id)} onNextMatchup={() => scrollToGame(nextGameHintId)} onPickComplete={handlePickComplete} />}
              </div>

              {/* ── Connector: R2 → S16 ── */}
              <div className="flex items-center justify-center">
                {showS16 ? (
                  <div className={`w-full h-[2px] ${info.connectorColor} rounded-full`} />
                ) : (
                  <div className={`w-full h-[2px] ${info.connectorColor} rounded-full`} />
                )}
              </div>

              {/* ── S16 column: rendered on even pairs, odd pairs get invisible placeholder ── */}
              {showS16 ? (
                <div className={`flex flex-col justify-center ${pairIdx > 0 ? (isMobile ? 'mt-2' : 'mt-3') : ''}`}>
                  {s16 && <Matchup game={s16} size={isMobile ? 'sm' : 'md'} compact={isMobile} showConfidence writeEnabled={Boolean(writeEnabled)} isNextGameHint={nextGameHintId === s16.id} isPathSource={isPathSourceForHint(s16.id)} onNextMatchup={() => scrollToGame(nextGameHintId)} onPickComplete={handlePickComplete} />}
                </div>
              ) : (
                <div className="invisible" />
              )}

              {/* ── Connector: S16 → E8 ── */}
              {showS16 ? (
                <div className="flex items-center justify-center">
                  <div className="w-full h-[2px] bg-amber-500/30 rounded-full" />
                </div>
              ) : (
                <div className="invisible" />
              )}

              {/* ── E8 column: rendered once, others invisible ── */}
              {showE8 ? (
                <div className="flex flex-col justify-center">
                  {eliteEightGame && (
                    <div className="relative">
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-400 bg-dark-700/90 px-2 py-0.5 rounded border border-amber-500/30">
                          Regional Final
                        </span>
                      </div>
                      <Matchup 
                        game={eliteEightGame} 
                        size={isMobile ? 'md' : 'lg'}
                        compact={isMobile}
                        showConfidence
                        writeEnabled={Boolean(writeEnabled)}
                        isNextGameHint={nextGameHintId === eliteEightGame.id}
                        isPathSource={isPathSourceForHint(eliteEightGame.id)}
                        onNextMatchup={() => scrollToGame(nextGameHintId)}
                        onPickComplete={handlePickComplete}
                        className="border-2 border-amber-500/30 bg-amber-500/[0.03] gold-glow-border rounded-xl"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="invisible" />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Region progress indicator */}
      <div className="mt-5 sm:mt-8 pt-4 sm:pt-6 border-t border-white/[0.06]">
        <div className="grid grid-cols-4 gap-2 sm:gap-4 text-center">
          <div>
            <div className="text-base sm:text-lg font-bold text-primary-400">
              {firstRoundGames.length}
            </div>
            <div className="text-[10px] sm:text-xs text-text-muted">Round 1</div>
          </div>
          <div>
            <div className="text-base sm:text-lg font-bold text-accent-400">
              {secondRoundGames.length}
            </div>
            <div className="text-[10px] sm:text-xs text-text-muted">Round 2</div>
          </div>
          <div>
            <div className="text-base sm:text-lg font-bold text-info-400">
              {sweetSixteenGames.length}
            </div>
            <div className="text-[10px] sm:text-xs text-text-muted">Sweet 16</div>
          </div>
          <div>
            <div className="text-base sm:text-lg font-bold text-amber-400">
              1
            </div>
            <div className="text-[10px] sm:text-xs text-text-muted">Elite 8</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BracketRegionComponent;
